import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  LabraToolTipV2,
  TextArea,
  TextInputV2,
} from '@labrav/react-components'
import React, { useState } from 'react'
import { faExternalLinkAlt } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './FlyOutProductRedirectionSettingsMicrosoft.styles'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import { FormikProps, useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import { FormLockedType } from '../../../types/FormLockedType'
import { FlyoutProductRedirectionSettingsFaqs } from './FlyoutProductRedirectionSettingsFaqs'

type FlyOutProductRedirectionSettingsProps = {
  detailView?: boolean
  detailViewTooltip?: string
  handleRefLinkClick?: (inex: number) => void
}

export const FlyOutProductRedirectionSettingsMicrosoft: React.FC<
  FormLockedType & FlyOutProductRedirectionSettingsProps
> = ({ isFormLocked, detailView, detailViewTooltip }) => {
  const { classes } = useStyles()
  const { values, errors, touched, submitCount, handleBlur, handleChange } =
    useFormikContext() as FormikProps<FlyoutProductSections>

  const infoCollectionFormMetaData = values.infoCollectionFormMetaData
  const infoCollectionFormMetaDataErrors = errors.infoCollectionFormMetaData
  const infoCollectionFormMetaDataTouched = touched.infoCollectionFormMetaData

  const applicationLoginPageURL = (
    <div
      className={classes.sectionContainer}
      data-testid="product-redirection-settings-login-page-url"
    >
      <div className={classes.textContainer}>
        <div className={classes.sectionHeadingContainer}>
          <Typography className={classes.sectionHeading}>
            Application login page URL
          </Typography>
        </div>

        <Typography className={classes.sectionText}>
          Please provide your application login page URL where the user will be
          redirected 5 seconds after submitting the form on the buyer
          registration page.
        </Typography>
      </div>
      <LabraToolTipV2
        followCursor={detailView}
        tooltipPlacement="bottom-start"
        description={detailViewTooltip}
        className={classes.fullWidth}
      >
        <div className={classes.textWidth}>
          <TextInputV2
            keyName="infoCollectionFormMetaData.redirectionSettings.applicationLoginPageUrl"
            title="Application login page URL"
            type="text"
            placeHolder=""
            value={
              infoCollectionFormMetaData?.redirectionSettings
                ?.applicationLoginPageUrl
            }
            updatedValue={
              infoCollectionFormMetaData?.redirectionSettings
                ?.applicationLoginPageUrl
            }
            maxNumber={72}
            isError={
              !!(
                infoCollectionFormMetaDataErrors?.redirectionSettings
                  ?.applicationLoginPageUrl &&
                infoCollectionFormMetaDataTouched?.redirectionSettings
                  ?.applicationLoginPageUrl
              ) ||
              (!!infoCollectionFormMetaDataErrors?.redirectionSettings
                ?.applicationLoginPageUrl &&
                submitCount > 0)
            }
            onChange={handleChange}
            isReadOnly={isFormLocked as boolean}
            isRequired={false}
            errorText={
              (!!infoCollectionFormMetaDataTouched?.redirectionSettings
                ?.applicationLoginPageUrl &&
                infoCollectionFormMetaDataErrors?.redirectionSettings
                  ?.applicationLoginPageUrl) ||
              submitCount > 0
                ? infoCollectionFormMetaDataErrors?.redirectionSettings
                    ?.applicationLoginPageUrl
                : ''
            }
            showSpinner={false}
            onBlur={handleBlur}
            viewOnly={detailView}
          />
        </div>
      </LabraToolTipV2>
    </div>
  )

  const optionalText =
    'Custom instructions that can be sent through email (optional)'

  const customInstructions = (
    <div
      className={classes.sectionContainer}
      data-testid="product-redirection-settings-custom-instructions"
    >
      <div className={classes.textContainer}>
        <div className={classes.sectionHeadingContainer}>
          <Typography className={classes.sectionHeading}>
            Custom instructions
          </Typography>
          <Typography className={classes.sectionText}>Optional</Typography>
        </div>
        <Typography className={classes.sectionText}>
          Please provide exact instructions on how buyers can gain access to
          your application. These instructions will be shared to the buyer in
          the purchase confirmation email, that is sent immediately after the
          buyer submits the form on the buyer registration page. (Refer no. 9 in
          image no. 4)
        </Typography>
      </div>
      <LabraToolTipV2
        followCursor={detailView}
        tooltipPlacement="bottom-start"
        description={detailViewTooltip}
        className={classes.fullWidth}
      >
        <TextArea
          keyName="infoCollectionFormMetaData.redirectionSettings.customInstructions"
          title={optionalText}
          rows={10}
          height={100}
          maxCharacters={1000}
          type="string"
          placeHolder=""
          value={
            infoCollectionFormMetaData?.redirectionSettings?.customInstructions
          }
          isError={
            !!(
              infoCollectionFormMetaDataTouched?.redirectionSettings
                ?.customInstructions &&
              infoCollectionFormMetaDataErrors?.redirectionSettings
                ?.customInstructions
            )
          }
          onChange={handleChange}
          isReadOnly={isFormLocked as boolean}
          isRequired={false}
          errorText={
            (!!infoCollectionFormMetaDataTouched?.redirectionSettings
              ?.customInstructions &&
              infoCollectionFormMetaDataErrors?.redirectionSettings
                ?.customInstructions) ||
            ''
          }
          showSpinner={false}
          onBlur={handleBlur}
          viewOnly={detailView}
          updatedValue={
            infoCollectionFormMetaData?.redirectionSettings?.customInstructions
          }
        />
      </LabraToolTipV2>
    </div>
  )
  const [indexModal, setIndexModal] = useState<number>(0)

  return (
    <BoxV2
      heading={'Buyer redirection settings'}
      headingIcon={
        <FontAwesomeIcon
          icon={faExternalLinkAlt}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <FlyoutProductRedirectionSettingsFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <div
        className={classes.sectionContainer}
        data-testid={'redirection-settings'}
      >
        {applicationLoginPageURL}
        {customInstructions}
      </div>
    </BoxV2>
  )
}
